/**
 * The API supplies an exclusive deadline and the frozen owner's timezone.
 *
 * With a timezone the answer is the number of whole local dates still AHEAD of
 * today: a 14-day trial reads 14 on its signup day and 0 on its final date,
 * which is still a live day (render it as "Ends today", and read liveness off
 * the deadline, never off this count). Until 2026-10-08 the count included
 * today, so a fresh signup read "15d left" against a 14-day promise and the
 * day a 7-day self-extension landed read 8. Dates, not hours: a DST date may
 * last 23 or 25 hours.
 *
 * Without a timezone (older responses, rollout off) the deadline is an exact
 * instant and the count is elapsed days rounded up: 1 inside the last 24
 * hours, 0 only once it has passed.
 */
export function getTrialDaysLeft(expiresAt, timeZone, now = Date.now()) {
    if (!expiresAt)
        return null;
    const deadline = new Date(expiresAt).getTime();
    if (!Number.isFinite(deadline))
        return null;
    if (deadline <= now)
        return 0;
    if (!timeZone)
        return Math.ceil((deadline - now) / 86_400_000);
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' });
    const dateNumber = (instant) => {
        const values = Object.fromEntries(parts.formatToParts(new Date(instant)).map(part => [part.type, part.value]));
        return Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day));
    };
    // The final available date is the one the deadline instant has just left.
    return (dateNumber(deadline - 1) - dateNumber(now)) / 86_400_000;
}
export function getTrialDeadlineCopy(expiresAt, timeZone) {
    const expiry = new Date(expiresAt);
    const day = new Intl.DateTimeFormat('en-US', { timeZone, month: 'long', day: 'numeric', year: 'numeric' });
    const cutoff = new Intl.DateTimeFormat('en-US', {
        timeZone, month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
    });
    return `Available through ${day.format(new Date(expiry.getTime() - 1))}. Ends ${cutoff.format(expiry)}.`;
}
//# sourceMappingURL=trialDeadline.js.map